// Waren und Vorräte (Umlaufvermögen) für den Risiken-Reiter.
//
// Die Werte liegen je Jahr als `wv_*` im `otherInfo`-Blob der Kennzahlen
// (`ContactKpi`) — zusammen mit dem Anlagevermögen aus der Vermögensmatrix
// ergeben sie die Summe, die die Inhaltsversicherung tragen muss.
//
// Der KENNZAHLEN-REITER ZEIGT SIE NICHT: Eine Versicherungssumme ist keine
// betriebswirtschaftliche Kennzahl und stand dort neben Umsatz und Rohertrag
// wie ein Fremdkörper. Die Ablage bleibt trotzdem, wo sie ist — sie ist
// jahresbezogen, und ein eigener Speicherort für vier Beträge wäre eine
// Migration ohne Gewinn.
//
// Angezeigt werden sie seit dem 08.10.2026 in der Vermögensmatrix unter der
// Kennzahl „Waren und Vorräte" (der eigene Abschnitt „Umlaufvermögen" ist
// entfallen). Neue Vorräte werden als Risikoobjekte mit Kennzahl erfasst; die
// Erfassungs-Helfer unten bleiben für den Bestand. Der Kennzahlen-Reiter kennt
// die Schlüssel (`kpiTypes.ts`), zeigt sie aber weiterhin nicht.
//
// React-frei, damit die Auswahl des Jahres und das Lesen der Beträge ohne DOM
// prüfbar bleiben.

import { parseEuroStrict } from "../kennzahlen/kpiFormat";

/** Rohform einer Jahreszeile, wie `risk-kpi/by-contacts` sie liefert. */
export interface WvJahrRoh {
  year: number;
  otherInfo?: string | null;
}

export interface WarenVorraete {
  /** Jahr, aus dem die Werte stammen — null, wenn nichts erfasst ist. */
  jahr: number | null;
  unverarbeitet: number | null;
  halbfertig: number | null;
  fertig: number | null;
  /** Benötigte Versicherungssumme laut Kunde (nicht die Summe der drei Posten). */
  gesamt: number | null;
  /** Bemerkung des Beraters zur Vorratslage — Freitext, kein Betrag. */
  hinweis: string | null;
  /**
   * Rohwerte, die keine eindeutige Zahl ergaben („ca. 2 Mio"). Sie fehlen in
   * jeder Summe und müssen deshalb sichtbar bleiben — eine Versicherungssumme,
   * die still zu niedrig ist, fällt erst im Schadenfall auf.
   */
  unklar: string[];
}

export const LEERE_WV: WarenVorraete = {
  jahr: null,
  unverarbeitet: null,
  halbfertig: null,
  fertig: null,
  gesamt: null,
  hinweis: null,
  unklar: [],
};

/**
 * Schlüssel, die ein Jahr zum „Vorratsjahr" machen. `wv_hinweis` steht
 * BEWUSST nicht dabei: ein Jahr, in dem nur eine Bemerkung steht, gewönne
 * sonst gegen ein Vorjahr mit vollständigen Beträgen.
 */
const WV_KEYS = ["wv_unverarbeitet", "wv_halbfertig", "wv_fertig", "wv_gesamt"] as const;

/** `otherInfo`-Blob eines Jahres → die vier Rohwerte (undefined = nicht erfasst). */
const rohwerte = (otherInfo: string | null | undefined): Record<string, unknown> => {
  if (!otherInfo) return {};
  try {
    const o = JSON.parse(otherInfo);
    return o && typeof o === "object" ? (o as Record<string, unknown>) : {};
  } catch {
    // Kein JSON — im Bestand steht dort teils Fließtext. Kein Grund, den
    // Risiken-Reiter zu weißen; dieses Jahr trägt dann eben keine Vorräte.
    return {};
  }
};

const istGefuellt = (v: unknown): boolean => v !== undefined && v !== null && v !== "";

/**
 * Jüngstes Jahr, das überhaupt einen Waren-/Vorratswert trägt.
 *
 * Nicht einfach das jüngste Jahr: die Kennzahlen werden oft für mehrere Jahre
 * angelegt, und das neueste ist regelmäßig erst zur Hälfte gefüllt. Ohne diese
 * Suche zeigte der Risiken-Reiter „keine Vorräte erfasst", obwohl das Vorjahr
 * vollständig ist.
 */
export const wvAusJahren = (jahre: WvJahrRoh[] | null | undefined): WarenVorraete => {
  const sortiert = [...(jahre ?? [])].sort((a, b) => b.year - a.year);
  for (const j of sortiert) {
    const roh = rohwerte(j.otherInfo);
    if (!WV_KEYS.some((k) => istGefuellt(roh[k]))) continue;
    const unklar: string[] = [];
    const zahl = (key: string): number | null => {
      const v = roh[key];
      if (!istGefuellt(v)) return null;
      const n = parseEuroStrict(v);
      if (n === null) unklar.push(String(v));
      return n;
    };
    return {
      jahr: j.year,
      unverarbeitet: zahl("wv_unverarbeitet"),
      halbfertig: zahl("wv_halbfertig"),
      fertig: zahl("wv_fertig"),
      gesamt: zahl("wv_gesamt"),
      hinweis: typeof roh["wv_hinweis"] === "string" && roh["wv_hinweis"].trim()
        ? String(roh["wv_hinweis"]).trim()
        : null,
      unklar,
    };
  }
  return LEERE_WV;
};

/**
 * Die drei Posten in der Reihenfolge der Fertigungstiefe (Rohstoff →
 * Zwischenprodukt → Fertigware). Dieselbe Gliederung wie § 266 Abs. 2 B I HGB
 * und wie die Sektion „Waren und Vorräte" im Kennzahlen-Reiter — die Labels
 * hier und dort müssen dieselben bleiben, sonst liest sich derselbe Betrag in
 * zwei Reitern als zwei verschiedene Dinge.
 */
export const WV_POSTEN = [
  {
    key: "unverarbeitet",
    label: "Unverarbeitete Erzeugnisse",
    hinweis: "Roh-, Hilfs- und Betriebsstoffe",
  },
  {
    key: "halbfertig",
    label: "Halbfertige Erzeugnisse",
    hinweis: "unfertige Erzeugnisse und Leistungen",
  },
  {
    key: "fertig",
    label: "Fertige Erzeugnisse und Waren",
    hinweis: "Fertigwaren und Handelsware",
  },
] as const satisfies ReadonlyArray<{
  key: "unverarbeitet" | "halbfertig" | "fertig";
  label: string;
  hinweis: string;
}>;

/** Summe der drei Posten — null, wenn keiner davon eine Zahl trägt. */
export const wvTeilsumme = (w: WarenVorraete): number | null => {
  const teile = [w.unverarbeitet, w.halbfertig, w.fertig].filter(
    (n): n is number => n !== null,
  );
  return teile.length > 0 ? teile.reduce((a, b) => a + b, 0) : null;
};

/**
 * Der Betrag, der in die Versicherungssumme eingeht.
 *
 * Die vom Kunden genannte Gesamtsumme hat Vorrang vor der Summe der Posten:
 * Wo beide vorliegen, ist die Aufteilung oft unvollständig (ein Posten ohne
 * Wert), die Gesamtsumme aber vollständig. Die kleinere Zahl zu nehmen hieße,
 * eine Unterversicherung in die Auswertung zu schreiben.
 */
export const wvVersicherungssumme = (w: WarenVorraete): number | null =>
  w.gesamt ?? wvTeilsumme(w);

/** Trägt der Datensatz überhaupt etwas? */
export const hatWv = (w: WarenVorraete): boolean =>
  w.jahr !== null && (wvVersicherungssumme(w) !== null || w.unklar.length > 0);

// ─────────────────────────────────────────────────────────────────────────────
// Erfassung
//
// Bis hierher ist die Datei eine reine Lese-Schicht. Ab hier kommt die
// Gegenrichtung: aus dem Blob eines Jahres die Rohtexte holen, sie ändern, den
// Blob zurückschreiben.
//
// Warum ROHTEXTE und nicht Zahlen: Im Bestand steht in diesen Feldern auch
// „ca. 2 Mio". Wer das über eine Zahl bearbeitet, verliert die Einschränkung im
// Moment des Ladens — das Feld käme als leer zurück und ein Speichern löschte
// den Vermerk, ohne dass jemand ihn je gesehen hätte. Der Editor arbeitet
// deshalb auf demselben Text, der in der Datenbank steht; geparst wird erst für
// die Anzeige.
// ─────────────────────────────────────────────────────────────────────────────

/** Die fünf erfassbaren Felder als Rohtext, so wie sie im Blob stehen. */
export interface WvEntwurf {
  unverarbeitet: string;
  halbfertig: string;
  fertig: string;
  gesamt: string;
  hinweis: string;
}

export const LEERER_WV_ENTWURF: WvEntwurf = {
  unverarbeitet: "",
  halbfertig: "",
  fertig: "",
  gesamt: "",
  hinweis: "",
};

/** Blob-Schlüssel je Entwurfsfeld — die einzige Stelle, die beide verbindet. */
const WV_FELD_KEY: Record<keyof WvEntwurf, string> = {
  unverarbeitet: "wv_unverarbeitet",
  halbfertig: "wv_halbfertig",
  fertig: "wv_fertig",
  gesamt: "wv_gesamt",
  hinweis: "wv_hinweis",
};

/**
 * Der vollständige `otherInfo`-Blob eines bestimmten Jahres.
 *
 * Vollständig, nicht nur die `wv_`-Schlüssel: Beim Speichern geht der ganze
 * Blob zurück ans Backend (`otherInfo` ist EIN Feld, kein Teil-Patch). Wer hier
 * nur die Vorratswerte behielte, löschte mit dem ersten Speichern jede andere
 * qualitative Angabe des Jahres.
 */
export const wvBlobAus = (
  jahre: WvJahrRoh[] | null | undefined,
  jahr: number,
): Record<string, unknown> => rohwerte((jahre ?? []).find((j) => j.year === jahr)?.otherInfo);

/** Die fünf Rohtexte eines Blobs; fehlende Schlüssel werden zu "". */
export const wvEntwurfAus = (blob: Record<string, unknown>): WvEntwurf => {
  const text = (key: string): string => {
    const v = blob[key];
    return v === undefined || v === null ? "" : String(v);
  };
  return {
    unverarbeitet: text(WV_FELD_KEY.unverarbeitet),
    halbfertig: text(WV_FELD_KEY.halbfertig),
    fertig: text(WV_FELD_KEY.fertig),
    gesamt: text(WV_FELD_KEY.gesamt),
    hinweis: text(WV_FELD_KEY.hinweis),
  };
};

/**
 * Blob mit den Werten des Entwurfs — alle übrigen Schlüssel unverändert.
 *
 * Ein geleertes Feld LÖSCHT seinen Schlüssel, statt "" zu schreiben. Ein
 * leerer String zählt zwar nirgends als Wert, machte das Jahr aber weiterhin
 * zum „Vorratsjahr" (`wvAusJahren` sucht nach vorhandenen Schlüsseln) — das
 * Verzeichnis zeigte dann ein Jahr mit vier Strichen statt auf das Vorjahr mit
 * echten Zahlen zurückzufallen.
 */
export const wvBlobMit = (
  blob: Record<string, unknown>,
  entwurf: WvEntwurf,
): Record<string, unknown> => {
  const neu = { ...blob };
  for (const [feld, key] of Object.entries(WV_FELD_KEY) as [keyof WvEntwurf, string][]) {
    const wert = entwurf[feld].trim();
    if (wert) neu[key] = wert;
    else delete neu[key];
  }
  return neu;
};

/** Trägt der Entwurf überhaupt etwas? Steuert „Speichern" und die Leer-Warnung. */
export const wvEntwurfGefuellt = (e: WvEntwurf): boolean =>
  Object.values(e).some((v) => v.trim() !== "");

/**
 * Entwurf → dieselbe Sicht, die auch aus der Datenbank käme.
 *
 * Damit zeigt die Tabelle beim Tippen exakt das, was nach dem Speichern dort
 * stehen wird — Summen, Striche und der „unklar"-Vermerk entstehen für beide
 * Zustände aus derselben Rechnung. Ein Betrag, der keine eindeutige Zahl ergibt
 * („ca. 2 Mio"), blockiert das Speichern NICHT: im Bestand ist das eine gültige
 * Aussage und oft alles, was der Kunde genannt hat. Er landet in `unklar` und
 * fehlt in jeder Summe — sichtbar, nicht verschwiegen.
 */
export const wvAusEntwurf = (e: WvEntwurf, jahr: number): WarenVorraete => {
  const unklar: string[] = [];
  const zahl = (roh: string): number | null => {
    const s = roh.trim();
    if (!s) return null;
    const n = parseEuroStrict(s);
    if (n === null) unklar.push(s);
    return n;
  };
  return {
    jahr,
    unverarbeitet: zahl(e.unverarbeitet),
    halbfertig: zahl(e.halbfertig),
    fertig: zahl(e.fertig),
    gesamt: zahl(e.gesamt),
    hinweis: e.hinweis.trim() || null,
    unklar,
  };
};
